'use client'

import { ReactNode, useCallback, useMemo } from 'react'
import type { MetaValueSchema } from '@/config/metadata-schemas.config'
import {
  parseStructuredWireValue,
  serializeStructuredWireValue,
  type BlobObjectRow,
} from '@/utils/metadata-form.utils'
import MetadataObjectListEditor from './MetadataObjectListEditor'
import styles from './MetadataManagementTool.module.css'

type Props = {
  id: string
  /** Field schema (array-of-objects or object with scalars + list props) */
  schema: MetaValueSchema
  /** Singular label for list items (derived from key when omitted) */
  itemLabel?: string
  value: string
  onChange: (next: string) => void
}

const humanize = (key: string): string => (
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase())
)

const defaultItemLabel = (schema: MetaValueSchema, explicit?: string): string => {
  if (explicit) {
    return explicit
  }
  if (schema.type === 'array') {
    return 'Item'
  }
  // object with a single array prop → use that prop name (sections → Section)
  const listKey = schema.properties
    && Object.entries(schema.properties).find(([, prop]) => prop.type === 'array')?.[0]
  if (listKey) {
    // sections → Section, slides → Slide
    const singular = listKey.endsWith('s') ? listKey.slice(0, -1) : listKey
    return humanize(singular)
  }
  return 'Item'
}

/**
 * Schema-driven structured editor for metadata blobs. Replaces the raw JSON
 * textarea for:
 * - array of objects (slider)
 * - object with scalar fields + array-of-object lists (pricing, exhibitionInformation)
 *
 * Always reads/writes the Ticketure wire JSON string.
 */
const MetadataBlobEditor = ({
  id,
  schema,
  itemLabel,
  value,
  onChange,
}: Props): ReactNode => {
  const parsed = useMemo(() => parseStructuredWireValue(value, schema), [value, schema])
  const label = defaultItemLabel(schema, itemLabel)

  const commit = useCallback((next: unknown) => {
    onChange(serializeStructuredWireValue(next, schema))
  }, [onChange, schema])

  // ── array-of-objects (slider) ──────────────────────────────────────────
  if (schema.type === 'array' && schema.items?.properties) {
    const rows = Array.isArray(parsed) ? parsed as BlobObjectRow[] : []
    return (
      <div className={styles.BlobEditor} data-testid="metadata-blob-editor">
        <MetadataObjectListEditor
          id={id}
          itemLabel={label}
          properties={schema.items.properties}
          value={rows}
          onChange={commit}
        />
      </div>
    )
  }

  // ── object with scalars + nested lists (pricing, exhibitionInformation) ─
  if (schema.type === 'object' && schema.properties) {
    const obj = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {}

    const updateScalar = (key: string, nextValue: string | boolean | number): void => {
      commit({ ...obj, [key]: nextValue })
    }

    const updateList = (key: string, nextList: BlobObjectRow[]): void => {
      commit({ ...obj, [key]: nextList })
    }

    return (
      <div className={styles.BlobEditor} data-testid="metadata-blob-editor">
        {Object.entries(schema.properties).map(([key, prop]) => {
          if (prop.type === 'array' && prop.items?.properties) {
            const list = Array.isArray(obj[key]) ? obj[key] as BlobObjectRow[] : []
            const listLabel = key.endsWith('s') ? humanize(key.slice(0, -1)) : humanize(key)
            return (
              <div key={key} className={styles.BlobSection}>
                <p className={styles.BlobSectionTitle}>{prop.description || humanize(key)}</p>
                <MetadataObjectListEditor
                  id={`${id}-${key}`}
                  itemLabel={listLabel}
                  properties={prop.items.properties}
                  value={list}
                  onChange={(next) => updateList(key, next)}
                />
              </div>
            )
          }

          const inputId = `${id}-${key}`
          const fieldLabel = prop.description || humanize(key)
          const fieldValue = obj[key]

          if (prop.type === 'boolean') {
            return (
              <label key={key} className={styles.BlobCheckboxLabel} htmlFor={inputId}>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={Boolean(fieldValue)}
                  onChange={(event) => updateScalar(key, event.target.checked)}
                />
                {fieldLabel}
              </label>
            )
          }

          if (prop.type === 'string' && prop.enum) {
            return (
              <div key={key} className={styles.BlobField}>
                <label className={styles.BlobFieldLabel} htmlFor={inputId}>{fieldLabel}</label>
                <select
                  id={inputId}
                  value={typeof fieldValue === 'string' ? fieldValue : ''}
                  onChange={(event) => updateScalar(key, event.target.value)}
                >
                  <option value="">—</option>
                  {prop.enum.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )
          }

          if (prop.type === 'number') {
            return (
              <div key={key} className={styles.BlobField}>
                <label className={styles.BlobFieldLabel} htmlFor={inputId}>{fieldLabel}</label>
                <input
                  id={inputId}
                  type="number"
                  value={fieldValue === undefined || fieldValue === '' ? '' : String(fieldValue)}
                  onChange={(event) => updateScalar(key, event.target.value)}
                />
              </div>
            )
          }

          const useTextarea = key === 'subtitle' || key === 'description' || key === 'content'
          return (
            <div key={key} className={styles.BlobField}>
              <label className={styles.BlobFieldLabel} htmlFor={inputId}>{fieldLabel}</label>
              {useTextarea
                ? (
                  <textarea
                    id={inputId}
                    value={typeof fieldValue === 'string' ? fieldValue : ''}
                    onChange={(event) => updateScalar(key, event.target.value)}
                    rows={2}
                  />
                )
                : (
                  <input
                    id={inputId}
                    type="text"
                    value={typeof fieldValue === 'string' ? fieldValue : ''}
                    onChange={(event) => updateScalar(key, event.target.value)}
                  />
                )}
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

export default MetadataBlobEditor
